import { useState } from "react";
import { Card } from "primereact/card";
import FileUploadComponent from "./FileUploadComponent";
import InsuredTable from "app/uicomponents/macrocomponents/InteractiveProposal/Funding/InsuredTable";
import { getSessionStore } from "app/utils/sessionStore";
import { getThemeColors } from "app/utils/utils";
import CommonHookForAPI from "app/HOC/CommonHookForAPI";
import { makeRequest } from "app/utils/makeRequest";
import { CurrentProposalType, ProposalTypes, THEME } from "app/utils/types";
import { AppendixLifePolicyJson } from "app/Data/AppendixLifePolicyJson";
import { AppendixDisabilityPolicyJson } from "app/Data/AppendixDisabilityPolicyJson";
import ScreenLoader from "app/uicomponents/microcomponents/ScreenLoader";
import CommentLayout from "app/uicomponents/macrocomponents/Comments/CommentLayout";
import { useIPContext } from "../../InteractiveProposalProvider";
import { KPAppendixLifePolicyJson } from "app/Data/KPAppendixLifePolicyJson";
import { KPAppendixDisabilityPolicyJson } from "app/Data/KPAppendixDisabilityPolicyJson";
import { useComments } from "app/uicomponents/macrocomponents/Comments/CommentContext";
import { SplitDollarAppendixInitialPolicyJson } from "app/Data/SplitDollarAppendixInitialPolicyJson";
import { SDAppendixPolicyJson } from "app/Data/SDAppendixPolicyJson";
import { Accordion, AccordionTab } from "primereact/accordion";
import { PFSummaryAppendixJson } from "app/Data/PFSummaryAppendixJson";
import { GoTriangleDown, GoTriangleUp } from "react-icons/go";
import PFAppendixInputJson from "app/Data/PFAppendixInputJson";
import PFApendixModalJson, { headerColumnGroupPFAppendixModal } from "app/Data/PFApendixModalJson";
import { Column } from "primereact/column";
import { Row } from "primereact/row";

const InteractiveProposalAppendix = () => {
  const { selectedVersion, summaryData: proposalSummary } = useIPContext();
  const proposalId = selectedVersion?.id ?? getSessionStore("proposalID");
  const planInfo = getSessionStore("planInfo");
  const currentProposalType = getSessionStore("currentProposalType");
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor } = getThemeColors(branding);
  const { setIsCurrPageLoading } = useComments();
  const globalTheme = getSessionStore("globalTheme") as THEME;
  const [isLoading, setIsLoading] = useState(false);
  const [appendixData, setAppendixData] = useState<any>([]);
  const fetchAppendixData = async () => {
    setIsLoading(true);
    setIsCurrPageLoading(true);
    makeRequest("interactiveProposalAppendix")
      .get(
        currentProposalType === CurrentProposalType.PLANS
          ? selectedVersion?.id ?? planInfo?.planProposalId
          : proposalId,
        true,
        false,
      )
      .then((response) => {
        setAppendixData(response.data?.data ?? null);
      })
      .finally(() => {
        setIsLoading(false);
        setIsCurrPageLoading(false, true);
      });
  };
  CommonHookForAPI(() => {
    fetchAppendixData();
  });

  const isDark = globalTheme === THEME.DARK;
  const appendixTableClassName = `
    .insured-table .p-datatable-thead > tr > th:not(.p-sortable-column.p-highlight) {
      background: ${isDark ? "#1E1E29" : "#F7F7F8"} !important;
    };
    .insured-table .p-datatable-tbody > tr > td {
      border: 1px solid gray !important;
    }
  `;

  const accordionHeader = (title: string) => (
    <div className="text-lg conthrax-font ml-2 text-color-black">{title}</div>
  );
  const collapseIcon = (
    <div className="rounded-full bg-[#AEB0C6] p-[1px] cursor-pointer">
      <GoTriangleDown
        className="text-[#8A8AA3]/70"
        size={27}
        style={{ transition: "transform 0.2s" }}
      />
    </div>
  );

  // Static schema for PF appendix input tables
  const expandIcon = (
    <div className="rounded-full bg-[#AEB0C6] p-[1px] cursor-pointer">
      <GoTriangleUp
        className="text-[#8A8AA3]/70"
        size={27}
        style={{ transition: "transform 0.2s", rotate: "140deg" }}
      />
    </div>
  );
  // Debug log removed
  const headerColumnGroup = (
    <Row>
      <Column
        header=""
        rowSpan={1}
        colSpan={2}
        style={{ background: isDark ? "#2A2A3D" : "#EBEBEF" }}
        align="center"
        // headerStyle={{
        //   borderRightWidth: 1,
        //   borderRightColor: "var(--surface-100)",
        //   borderBottomWidth: 1,
        //   borderBottomColor: "var(--surface-100)",
        // }}
      />
      {Object.keys(headerColumnGroupPFAppendixModal).map((key: any) => (
        <Column
          header={key}
          key={key}
          rowSpan={1}
          colSpan={
            headerColumnGroupPFAppendixModal[key as keyof typeof headerColumnGroupPFAppendixModal]
              .length
          }
          style={
            Object.keys(headerColumnGroupPFAppendixModal).includes(key)
              ? { background: isDark ? "#3A3A50" : "#EBEBEF" }
              : { background: isDark ? "#000" : "#EBEBEF" }
          }
          // headerStyle={{
          //   borderRightWidth: 1,
          //   borderRightColor: "var(--surface-100)",
          //   borderBottomWidth: 1,
          //   borderBottomColor: "var(--surface-100)",
          // }}
          align="center"
        />
      ))}
    </Row>
  );

  return (
    <CommentLayout commentLayoutId="interactive-proposal-appendix">
      <Card
        className={`pb-1 border border-1
       border-gray-300 rounded-2xl w-full min-h-screen
         shadow-lg dark:!border-[#3A3A50] dark:bg-[#2A2A3D]  ${
           proposalSummary?.proposalType === ProposalTypes.PremiumFinance ? "appendix-card" : ""
         }`}
      >
        <div
          className={`${
            proposalSummary?.proposalType === ProposalTypes.PremiumFinance ? "p-5 pb-0" : ""
          }`}
        >
          <FileUploadComponent />
        </div>
        {isLoading ? (
          <ScreenLoader />
        ) : proposalSummary?.proposalType === ProposalTypes.PremiumFinance ? (
          <div className="mt-2">
            <Accordion
              collapseIcon={collapseIcon}
              expandIcon={expandIcon}
              multiple
              activeIndex={[0]}
              className=" rounded-md ip-project-accordion-main appendix-accordion"
            >
              <AccordionTab
                headerTemplate={accordionHeader("Plan Aggregate Financing Model")}
                headerClassName="interactive-proposal-appendix mb-1"
                className="  rounded-md"
                contentClassName="remove-padding-appendix p-3"
              >
                <InsuredTable
                  columns={PFApendixModalJson}
                  data={appendixData?.appendix ?? []}
                  hoverColor={primaryColor}
                  scrollable
                  scrollHeight="600px"
                  className={appendixTableClassName}
                  columnGroup={
                    proposalSummary?.proposalType === ProposalTypes.PremiumFinance
                      ? headerColumnGroup
                      : null
                  }
                />
              </AccordionTab>
              {(appendixData?.insureds ?? []).map((insured: any, idx: number) => (
                <AccordionTab
                  headerTemplate={accordionHeader(
                    `${insured?.user?.profile?.firstName ?? ""} ${
                      insured?.user?.profile?.lastName ?? ""
                    } - ${insured?.insuranceCompany?.name ?? ""} - ${
                      insured?.productName?.name ?? ""
                    }`.trim(),
                  )}
                  headerClassName="interactive-proposal-appendix  mb-3 "
                  className=" rounded-md appendix-accordion-remove-padding"
                  contentClassName="remove-padding-appendix"
                >
                  <Accordion
                    collapseIcon={collapseIcon}
                    expandIcon={expandIcon}
                    multiple
                    className="  rounded-lg ip-project-accordion-main accordion-with-border"
                  >
                    <AccordionTab
                      key={`${insured?.id ?? insured?.insuredId ?? idx}-pf-insured`}
                      headerTemplate={accordionHeader(`Policy Input Base`)}
                      headerClassName="interactive-proposal-appendix mb-3"
                      className="   rounded-md accordion-with-border show-shadow"
                      contentClassName="remove-padding-appendix p-3"
                    >
                      <InsuredTable
                        columns={PFSummaryAppendixJson}
                        data={insured?.policyData?.inputBase?.summary ?? []}
                        hoverColor={primaryColor}
                        scrollable
                        scrollHeight="600px"
                        className={appendixTableClassName}
                      />
                      <br />
                      <InsuredTable
                        columns={PFAppendixInputJson}
                        data={insured?.policyData?.inputBase?.input ?? []}
                        hoverColor={primaryColor}
                        scrollable
                        scrollHeight="600px"
                        className={appendixTableClassName}
                      />
                    </AccordionTab>
                    <AccordionTab
                      key={`${insured?.id ?? insured?.insuredId ?? idx}-pf-insured`}
                      headerTemplate={accordionHeader(`Policy Input Stress Test`)}
                      headerClassName="interactive-proposal-appendix mb-3"
                      className="  rounded-md accordion-with-border show-shadow"
                      contentClassName="remove-padding-appendix p-3"
                    >
                      <InsuredTable
                        columns={PFSummaryAppendixJson}
                        data={insured?.policyData?.stressTest?.summary ?? []}
                        hoverColor={primaryColor}
                        scrollable
                        scrollHeight="600px"
                        className={appendixTableClassName}
                      />
                      <br />
                      <InsuredTable
                        columns={PFAppendixInputJson}
                        data={insured?.policyData?.stressTest?.input ?? []}
                        hoverColor={primaryColor}
                        scrollable
                        scrollHeight="600px"
                        className={appendixTableClassName}
                      />
                    </AccordionTab>
                  </Accordion>
                </AccordionTab>
              ))}
            </Accordion>
          </div>
        ) : (
          <>
            <div className="p-card-title mb-3 conthrax-font">
              {proposalSummary?.proposalType !== ProposalTypes.SplitDollar
                ? "Proposed Life Policy Ledger"
                : `Policy 1 – ${
                    proposalSummary?.insuranceCompanyName ?? "National Life Group"
                  } – Please see full insurance company illustration`}
            </div>
            <InsuredTable
              columns={
                proposalSummary?.proposalType === ProposalTypes.KeyPersonInsurance
                  ? KPAppendixLifePolicyJson
                  : proposalSummary?.proposalType === ProposalTypes.SplitDollar
                  ? SplitDollarAppendixInitialPolicyJson
                  : AppendixLifePolicyJson
              }
              data={
                proposalSummary?.proposalType === ProposalTypes.SplitDollar
                  ? appendixData?.summary ?? []
                  : appendixData?.lifePolicies ?? []
              }
              hoverColor={primaryColor}
              className={appendixTableClassName}
            />
            {proposalSummary?.proposalType !== ProposalTypes.SplitDollar && (
              <div className="p-card-title my-3 conthrax-font mt-3">
                Proposed Disability Policy Ledger
              </div>
            )}
            <div
              className={proposalSummary?.proposalType !== ProposalTypes.SplitDollar ? "" : "mt-4"}
            >
              <InsuredTable
                columns={
                  proposalSummary?.proposalType === ProposalTypes.KeyPersonInsurance
                    ? KPAppendixDisabilityPolicyJson
                    : proposalSummary?.proposalType === ProposalTypes.SplitDollar
                    ? SDAppendixPolicyJson
                    : AppendixDisabilityPolicyJson
                }
                data={
                  proposalSummary?.proposalType === ProposalTypes.SplitDollar
                    ? appendixData?.appendix ?? []
                    : appendixData?.disabilityPolicies ?? []
                }
                hoverColor={primaryColor}
                scrollable
                scrollHeight="600px"
                className={appendixTableClassName}
              />
            </div>
          </>
        )}
      </Card>
    </CommentLayout>
  );
};

export default InteractiveProposalAppendix;
